import type { AxiosRequestConfig, AxiosResponse, Method, } from "axios"
import to from "await-to-js"
import axios, { AxiosError, } from "axios"
import qs from "qs"
import { IMPORT_META_ENV, } from "@/constants"
import { showMessage, } from "@/utils/messageBox"

const fetch = axios.create({
  baseURL: import.meta.env.DEV ? undefined : IMPORT_META_ENV.GMS_BASE_URL,
  adapter: import.meta.env.DEV ? "xhr" : "fetch",
  timeout: 10000,
  paramsSerializer(params,) {
    return qs.stringify(params, {
      arrayFormat: "comma",
    },)
  },
},)

function getErrorMsg() {
  return {
    CLIENT_ERROR: "客户端出错，请稍后再试!",
    SERVER_ERROR: "服务器出错，请稍后再试!",
    DEFAULT_ERROR: "未知错误，请稍后再试!",
  }
}

enum ResponseCode {
  REDIRECTION = 302,
  UNAUTHORIZED = 401,
}

interface UnAuthorizedResponse {
  redirectUrl: string
}

function handleError(msg: string = getErrorMsg().DEFAULT_ERROR,) {
  showMessage(msg, "error",)
}

interface GorgiasHeaders {
  token: string
  customer: string
  order: string
  shop: string
}

const {
  // getToken,
  removeToken,
  getGorgiasToken,
  getGorgiasCustomer,
  getGorgiasOrder,
  getGorgiasShop,
} = useToken()

const { start, done, } = useNProgress()

/** Gorgias Headers */
function getGorgiasHeaders(): Partial<Record<keyof GorgiasHeaders, string | null>> {
  return {
    token: getGorgiasToken(),
    customer: getGorgiasCustomer(),
    order: getGorgiasOrder(),
    shop: getGorgiasShop(),
  }
}

fetch.interceptors.request.use((config,) => {
  start()
  if (config.headers) {
    // const { localeState, } = useLocale()
    // config.headers["x-referer"] = location.href
    // config.headers["x-language"] = localeState.value
    // if (getToken()) {
    //   config.headers.Authorization = getToken()
    // }
    // 设置 Gorgias 相关的 headers
    const gorgiasHeaders = getGorgiasHeaders()
    Object.entries(gorgiasHeaders,).forEach(([key, value,],) => {
      if (value) {
        config.headers![`x-gorgias-${key}`] = value
      }
    },)
  }
  return config
}, (error,) => {
  done()
  handleError(error.message || getErrorMsg().CLIENT_ERROR,)
  return Promise.reject(error,)
},)

fetch.interceptors.response.use((response: AxiosResponse<ResponseData<UnAuthorizedResponse>>,) => {
  done()
  const { data, config, } = response
  if ((config.fetchOptions as Record<string, any>)?.rawData) {
    return response
  }

  if ("code" in data && (ResponseCode.REDIRECTION === data.code || ResponseCode.UNAUTHORIZED === data.code)) {
    removeToken()
    location.replace(data.data.redirectUrl,)
    return response
  }

  if ("code" in data) {
    // 失败
    if (data.code === 1) {
      handleError(data.msg || getErrorMsg().SERVER_ERROR,)
      return Promise.reject(response,)
    }
    return response
  }
  return response
}, (error,) => {
  done()
  if (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED) {
    return
  }

  if ((ResponseCode.REDIRECTION === error.response?.status || ResponseCode.UNAUTHORIZED === error.response?.status)) {
    removeToken()
    const redirectUrl = error.response.data.data.redirectUrl
    location.replace(redirectUrl,)
    return Promise.reject(error,)
  }

  handleError(error.message || getErrorMsg().SERVER_ERROR,)
  return Promise.reject(error,)
},)

async function formatRequest<T,>(method: Method, config: AxiosRequestConfig, headers: AxiosRequestConfig["headers"] = {},) {
  const formatConfig = {
    method,
    ...config,
    headers: {
      ...headers,
      ...config.headers,
    },
  }

  const [error, response,] = await to<AxiosResponse<T>>(
    fetch.request(formatConfig,),
  )

  if (!error && response) {
    return response.data
  }
}

// GET 请求方法
export function get<T, >(config: AxiosRequestConfig,) {
  return formatRequest<T>("GET", config,)
}

// POST JSON 请求方法
export function postJSON<T, >(config: AxiosRequestConfig,) {
  return formatRequest<T>("POST", config, {
    ...config.headers,
    "Content-Type": "application/json",
  },)
}

export function postFormData<T, >(config: AxiosRequestConfig,) {
  return formatRequest<T>("POST", config, {
    ...config.headers,
    "Content-Type": "multipart/form-data",
  },)
}

export { fetch, }
